import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import "./performanceInsights.scss";
import images from "../../../../utilities/images/images";
import analyticsApi from "../../../../redux/exam_analytics/analyticsApi";

// Share in-flight/completed responses so React Strict Mode / remounts
// do not hit getPerformanceInsights twice for the same params.
const performanceInsightsCache = new Map();

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const formatChange = (value, { prefixPositive = false } = {}) => {
  if (value === null || value === undefined || value === "") return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (/^[+\-]/.test(raw) || raw.includes("%")) return raw;
  if (prefixPositive) return `+${raw}`;
  return raw;
};

const mapInsightCard = (item = {}, tone = "green") => {
  const subject =
    item.subject ||
    item.title ||
    item.name ||
    item.label ||
    item.metric ||
    item.type ||
    "";
  const description =
    item.description ||
    item.message ||
    item.text ||
    item.insight ||
    item.subtitle ||
    "";
  const change = formatChange(
    item.change ??
      item.value ??
      item.badge ??
      item.score ??
      item.percentage ??
      item.count,
    { prefixPositive: tone === "green" }
  );

  if (!subject && !description) return null;

  return {
    subject: subject || "Insight",
    change,
    description,
    color: tone,
  };
};

const mapTimeCard = (item = {}) => {
  const title =
    item.title ||
    item.zone_name ||
    item.name ||
    item.label ||
    item.subject ||
    "";
  const subtitle =
    item.subtitle || item.zone_type || item.type || item.tag || "";
  const percentage = formatChange(
    item.percentage ?? item.percent ?? item.value ?? item.change
  );
  const description =
    item.description || item.message || item.text || item.hint || "";

  if (!title && !description && !percentage) return null;

  return {
    title: title || "Time insight",
    subtitle,
    percentage,
    description,
  };
};

const buildFallbackFromMetrics = (data = {}) => {
  const accuracy = data.accuracy;
  const percentile = data.percentile;
  const totalIncorrect = data.total_incorrect ?? data.incorrect ?? data.wrong;
  const totalUnattempt =
    data.total_un_attempt ?? data.unattempted ?? data.skipped;

  const strengths = [];
  if (accuracy !== undefined && accuracy !== null && accuracy !== "") {
    strengths.push({
      subject: "Accuracy",
      change: formatChange(`${accuracy}%`, { prefixPositive: true }),
      description: `Good accuracy (${accuracy}%). Keep improving!`,
      color: "green",
    });
  }
  if (percentile !== undefined && percentile !== null && percentile !== "") {
    strengths.push({
      subject: "Percentile",
      change: formatChange(`${percentile}%`, { prefixPositive: true }),
      description: `Your percentile score is decent (${percentile}%).`,
      color: "green",
    });
  }

  const improvements = [];
  if (
    totalIncorrect !== undefined &&
    totalIncorrect !== null &&
    totalIncorrect !== ""
  ) {
    improvements.push({
      subject: "Incorrect Answers",
      change: `-${totalIncorrect}`,
      description: `You answered ${totalIncorrect} questions incorrectly. Focus on revising these topics.`,
      color: "red",
    });
  }
  if (
    totalUnattempt !== undefined &&
    totalUnattempt !== null &&
    totalUnattempt !== ""
  ) {
    improvements.push({
      subject: "Unattempted Questions",
      change: `-${totalUnattempt}`,
      description: `You left ${totalUnattempt} questions unattempted.`,
      color: "red",
    });
  }

  const zones = asArray(data.time_quality_report?.zones || data.zones);
  const timeManagement = zones.map(mapTimeCard).filter(Boolean);

  return { strengths, improvements, timeManagement };
};

export const normalizePerformanceInsights = (payload, fallbackAnalytics) => {
  const root = payload?.data ?? payload ?? {};
  const data =
    root?.performance_insights && typeof root.performance_insights === "object"
      ? root.performance_insights
      : root;

  let strengths = asArray(
    data.your_strengths ||
      data.strengths ||
      data.strength ||
      data.positive_insights ||
      data.good_points
  )
    .map((item) => mapInsightCard(item, "green"))
    .filter(Boolean);

  let improvements = asArray(
    data.areas_to_improve ||
      data.improvements ||
      data.improve ||
      data.negative_insights ||
      data.weak_points
  )
    .map((item) => mapInsightCard(item, "red"))
    .filter(Boolean);

  const timeSource =
    data.time_management ||
    data.timeManagement ||
    data.time_insights ||
    data.time_quality_report ||
    data.time_quality;

  let timeManagement = [];
  if (Array.isArray(timeSource)) {
    timeManagement = timeSource.map(mapTimeCard).filter(Boolean);
  } else if (timeSource && typeof timeSource === "object") {
    const zones = asArray(timeSource.zones || timeSource.data || timeSource);
    timeManagement = zones.map(mapTimeCard).filter(Boolean);
  }

  const hasStructured =
    strengths.length > 0 || improvements.length > 0 || timeManagement.length > 0;

  if (!hasStructured) {
    const fromApiFlat = buildFallbackFromMetrics(data);
    strengths = fromApiFlat.strengths;
    improvements = fromApiFlat.improvements;
    timeManagement = fromApiFlat.timeManagement;
  }

  if (
    !strengths.length &&
    !improvements.length &&
    !timeManagement.length &&
    fallbackAnalytics
  ) {
    return buildFallbackFromMetrics(fallbackAnalytics);
  }

  return { strengths, improvements, timeManagement };
};

const PerformanceInsightsSkeleton = () => (
  <div
    className="performance_insights_container performance_insights_container--skeleton"
    aria-busy="true"
    aria-label="Loading performance insights"
  >
    <div className="performance_insights_header">
      <span className="pi-skeleton pi-skeleton--icon" />
      <span className="pi-skeleton pi-skeleton--title" />
    </div>

    <div className="insight_section">
      <span className="pi-skeleton pi-skeleton--section" />
      <div className="pi-skeleton-card">
        <div className="pi-skeleton-card__row">
          <span className="pi-skeleton pi-skeleton--label" />
          <span className="pi-skeleton pi-skeleton--badge" />
        </div>
        <span className="pi-skeleton pi-skeleton--line" />
        <span className="pi-skeleton pi-skeleton--line pi-skeleton--line-short" />
      </div>
      <div className="pi-skeleton-card">
        <div className="pi-skeleton-card__row">
          <span className="pi-skeleton pi-skeleton--label" />
          <span className="pi-skeleton pi-skeleton--badge" />
        </div>
        <span className="pi-skeleton pi-skeleton--line" />
      </div>
    </div>

    <div className="insight_section">
      <span className="pi-skeleton pi-skeleton--section" />
      <div className="pi-skeleton-card">
        <div className="pi-skeleton-card__row">
          <span className="pi-skeleton pi-skeleton--label" />
          <span className="pi-skeleton pi-skeleton--badge" />
        </div>
        <span className="pi-skeleton pi-skeleton--line" />
      </div>
      <div className="pi-skeleton-card">
        <div className="pi-skeleton-card__row">
          <span className="pi-skeleton pi-skeleton--label" />
          <span className="pi-skeleton pi-skeleton--badge" />
        </div>
        <span className="pi-skeleton pi-skeleton--line pi-skeleton--line-short" />
      </div>
    </div>
  </div>
);

const PerformanceInsights = ({ analytics }) => {
  const { paperId, testType, testDetails } = useSelector((s) => s.examSlice);
  const { selectedAttemptId } = useSelector((s) => s.analyticsSlice);
  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;

  const [insights, setInsights] = useState({
    strengths: [],
    improvements: [],
    timeManagement: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const requestKey = useMemo(() => {
    const finalPaperId =
      paperId ||
      testDetails?.paperId ||
      testDetails?.paper_id ||
      analytics?.paper_id;
    const finalSetId = testDetails?.setId || testDetails?.set_id;
    const paperType =
      testType?.id ?? testDetails?.paperType ?? testDetails?.paper_type;

    // Prefer attempt from the loaded report so we don't first call with 0,
    // then again when selectedAttemptId syncs from the dropdown.
    const attemptFromReport = analytics?.selected_attempt;
    const attemptCount =
      attemptFromReport !== undefined && attemptFromReport !== null
        ? Number(attemptFromReport)
        : selectedAttemptId !== undefined &&
          selectedAttemptId !== null &&
          Number(selectedAttemptId) !== 0
        ? Number(selectedAttemptId)
        : undefined;

    return JSON.stringify({
      paperId: finalPaperId ?? null,
      setId: finalSetId ?? null,
      paperType: paperType ?? null,
      attemptCount: attemptCount ?? null,
      plannerTestId:
        testDetails?.planner_test_id || testDetails?.testId || null,
    });
  }, [
    analytics?.paper_id,
    analytics?.selected_attempt,
    paperId,
    selectedAttemptId,
    testDetails?.paperId,
    testDetails?.paper_id,
    testDetails?.setId,
    testDetails?.set_id,
    testDetails?.paperType,
    testDetails?.paper_type,
    testDetails?.planner_test_id,
    testDetails?.testId,
    testType?.id,
  ]);

  useEffect(() => {
    let cancelled = false;
    const params = JSON.parse(requestKey || "{}");

    if (!params.paperId && !params.setId) {
      setInsights(
        normalizePerformanceInsights(null, analyticsRef.current)
      );
      setLoading(false);
      return undefined;
    }

    const applyResult = (response, errMessage = null) => {
      if (cancelled) return;
      setError(errMessage);
      setInsights(
        normalizePerformanceInsights(response, analyticsRef.current)
      );
      setLoading(false);
    };

    const cached = performanceInsightsCache.get(requestKey);
    if (cached?.status === "done") {
      applyResult(cached.response, cached.error);
      return undefined;
    }

    setLoading(true);
    setError(null);

    let requestPromise = cached?.promise;
    if (!requestPromise) {
      requestPromise = analyticsApi
        .getPerformanceInsights({
          paperId: params.paperId,
          setId: params.setId,
          paperType: params.paperType,
          attemptCount: params.attemptCount,
          plannerTestId: params.plannerTestId,
        })
        .then((response) => {
          performanceInsightsCache.set(requestKey, {
            status: "done",
            response,
            error: null,
          });
          return response;
        })
        .catch((err) => {
          const message =
            err?.message || "Failed to load performance insights";
          performanceInsightsCache.set(requestKey, {
            status: "done",
            response: null,
            error: message,
          });
          throw err;
        });

      performanceInsightsCache.set(requestKey, {
        status: "pending",
        promise: requestPromise,
      });
    }

    requestPromise
      .then((response) => applyResult(response))
      .catch((err) => {
        applyResult(
          null,
          err?.message || "Failed to load performance insights"
        );
      });

    return () => {
      cancelled = true;
    };
  }, [requestKey]);

  const { strengths, improvements, timeManagement } = insights;
  const hasAnyContent =
    strengths.length > 0 ||
    improvements.length > 0 ||
    timeManagement.length > 0;

  if (loading) {
    return <PerformanceInsightsSkeleton />;
  }

  if (!hasAnyContent) {
    return null;
  }

  return (
    <div className="performance_insights_container">
      <div className="performance_insights_header">
        <img
          src={images.Arrow_Growth_Icon}
          alt="Arrow Growth Icon"
          className="growth_icon"
        />
        <h3 className="performance_insights_title">Performance Insights</h3>
      </div>

      {error ? (
        <p className="performance_insights_error">{error}</p>
      ) : null}

      {strengths.length > 0 ? (
        <div className="insight_section strengths">
          <h4 className="section_title">
            <img src={images.tick_icon} alt="Tick Icon" className="tick_icon" />
            <span>Your Strengths</span>
          </h4>

          {strengths.map((item, idx) => (
            <div key={`strength-${idx}`} className="insight_card green_card">
              <div className="insight_card_header green_header">
                <span className="subject">{item.subject}</span>
                <span className="change positive">{item.change}</span>
              </div>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      ) : null}

      {improvements.length > 0 ? (
        <div className="insight_section improve">
          <h4 className="section_title">
            <img
              src={images.improve_icon}
              alt="Improve Icon"
              className="improve_icon"
            />
            <span>Areas to Improve</span>
          </h4>

          {improvements.map((item, idx) => (
            <div key={`improve-${idx}`} className="insight_card red_card">
              <div className="insight_card_header red_header">
                <span className="subject">{item.subject}</span>
                <span className="change negative">{item.change}</span>
              </div>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      ) : null}

      {timeManagement.length > 0 ? (
        <div className="insight_section time">
          <h4 className="section_title">
            <img src={images.time_icon} alt="Time Icon" className="time_icon" />
            <span>Time Management</span>
          </h4>

          {timeManagement.map((item, idx) => (
            <div key={`time-${idx}`} className="insight_card yellow_card">
              <div className="insight_card_header yellow_header">
                <span className="subject">{item.title}</span>
                <span className="change">{item.percentage}</span>
              </div>

              <ul>
                {item.subtitle ? <li>{item.subtitle}</li> : null}
                {item.description ? <li>{item.description}</li> : null}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default PerformanceInsights;
